import React, { useState, useMemo, useRef, useEffect } from "react";
import { toast } from "sonner";
import { createErrorCopyAction } from "./ui/Toaster";
import { useBucketCollections } from "../hooks/useBucketCollections";
import { SubscriptionClient } from "../sdk";
import { formatCurrency, formatPercentage } from "../sdk/utils/display";
import {
  LoadingMessage,
  ErrorMessage,
  EmptyMessage,
  WarningMessage,
  LayoutHeader,
  TableHeaderCell,
} from "./smartlayout/fragments";
import {
  TransferModal,
  TransferModalConfirmParams,
} from "./smartlayout/modals";
import {
  DEFAULT_BASE_URL,
  DEFAULT_HEIGHT_BUCKET_COLLECTIONS,
  DEFAULT_REFRESH_INTERVAL,
} from "./smartlayout/constants";
import { useFiClient } from "../hooks/useFiClient";
import "./smartlayout/layout.css";
import { Bucket, calculateRiskRatio, getDisplayMarginType } from "../sdk";
import type {
  ExtractToBucketResponse,
  BucketTransferResponse,
  WithdrawFromBucketResponse,
} from "../sdk/types/api";

interface BucketCollectionsProps {
  baseURL: string;
  subscriptionClient: SubscriptionClient;
  walletAddress: string;
  mnemonic: string; // Required for transfer operations
  authToken?: string;
  height?: string;
  refreshInterval?: number;
  onBucketSelect?: (bucketId: string, totalEquity: number) => void;
}

type SortField =
  | "marginType"
  | "usedMargin"
  | "totalEquity"
  | "riskRatio"
  | "bucketId";
type SortDirection = "asc" | "desc";

function BucketCollections({
  baseURL = DEFAULT_BASE_URL,
  subscriptionClient,
  authToken,
  walletAddress,
  mnemonic,
  height = DEFAULT_HEIGHT_BUCKET_COLLECTIONS,
  refreshInterval = DEFAULT_REFRESH_INTERVAL,
  onBucketSelect,
}: BucketCollectionsProps): JSX.Element {
  const {
    collections,
    loading,
    error,
    spotWalletBalance,
    isConnected,
    refreshBucketBalance,
  } = useBucketCollections({
    subscriptionClient,
    baseURL,
    authToken,
    walletAddress,
    refreshInterval,
  });

  // Removed debug logging for better performance

  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [selectedBucketId, setSelectedBucketId] = useState<string>("");
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [transferFrom, setTransferFrom] = useState<string>("");
  const [transferTo, setTransferTo] = useState<string>("");
  const [transferAmount, setTransferAmount] = useState<number>(10);
  const [createBucketAmount, setCreateBucketAmount] = useState<number>(10);
  const [createBucketType, setCreateBucketType] = useState<string>("");
  const [createMarginType, setCreateMarginType] = useState<
    "cross" | "isolated"
  >("cross");
  const [transferMarginType, setTransferMarginType] = useState<
    "cross" | "isolated"
  >("cross");
  const [activeTab, setActiveTab] = useState<"transfer" | "create">("transfer");
  const [isTransferring, setIsTransferring] = useState<boolean>(false);
  const [filterMode, setFilterMode] = useState<
    "all" | "non-zero" | "dormant" | "active"
  >("non-zero");
  const { client: exchangeClient, isReady: exchangeReady, error: exchangeError } = useFiClient({
    baseURL,
    mnemonic: mnemonic ?? "",
  });
  // Ref to store the loading toast ID so we can dismiss it later
  const loadingToastIdRef = useRef<number>(0);

  useEffect(() => {
    if (exchangeError) {
      const msg = exchangeError.message;
      console.error("Failed to initialize FiClient:", exchangeError);
      toast.error("Exchange client initialization failed", {
        description: msg,
        action: createErrorCopyAction(msg),
      });
    }
  }, [exchangeError]);

  // Sort and filter collections
  const sortedCollections = useMemo(() => {
    // Collections are already filtered in the hook, but double-check for safety
    let validCollections = collections.filter(
      (c) =>
        c &&
        c.bucketId &&
        typeof c.bucketId === "string" &&
        c.bucketId.trim() !== "",
    );

    // Apply filters based on filterMode
    if (filterMode === "non-zero") {
      validCollections = validCollections.filter(
        (c) => (c.totalEquity || 0) > 0,
      );
    } else if (filterMode === "dormant") {
      validCollections = validCollections.filter(
        (c) => c.status?.toLowerCase() === "dormant",
      );
    } else if (filterMode === "active") {
      validCollections = validCollections.filter(
        (c) => c.status?.toLowerCase() === "active",
      );
    }
    // "all" mode shows all valid collections without additional filtering

    if (!sortField) {
      return validCollections;
    }

    return [...validCollections].sort((a, b) => {
      let aValue: number | string = 0;
      let bValue: number | string = 0;

      switch (sortField) {
        case "marginType":
          aValue = getDisplayMarginType(a) || "";
          bValue = getDisplayMarginType(b) || "";
          break;
        case "usedMargin":
          aValue = a.usedMargin || 0;
          bValue = b.usedMargin || 0;
          break;
        case "totalEquity":
          aValue = a.totalEquity || 0;
          bValue = b.totalEquity || 0;
          break;
        case "riskRatio":
          aValue = calculateRiskRatio(a);
          bValue = calculateRiskRatio(b);
          break;
        case "bucketId":
          aValue = a.bucketId || "";
          bValue = b.bucketId || "";
          break;
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortDirection === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      const numA = Number(aValue);
      const numB = Number(bValue);

      return sortDirection === "asc" ? numA - numB : numB - numA;
    });
  }, [collections, sortField, sortDirection, filterMode]);

  /** Map asset symbol to asset registry index (e.g. USDC -> 1). Defaults to 1 for unknown. */
  const assetSymbolToCollateralIndex = (symbol: string): number => {
    const m: Record<string, number> = { USDC: 1, USDT: 2 };
    return m[symbol?.toUpperCase()] ?? 1;
  };

  /**
   * Helper function to find a dormant or empty bucket matching the margin type and bucket type
   * Returns the first matching bucket, or null if none found
   */
  const findDormantOrEmptyBucket = (
    marginType: "cross" | "isolated",
    bucketType: string,
  ): Bucket | null => {
    const normalizedMarginType = marginType.toLowerCase();
    const normalizedBucketType = bucketType.toUpperCase();
    const expectedIndex =
      normalizedBucketType &&
      String(assetSymbolToCollateralIndex(normalizedBucketType));
    return (
      sortedCollections.find((bucket) => {
        const bucketMarginType = getDisplayMarginType(bucket)?.toLowerCase();
        const isDormant = bucket.status.toLowerCase() === "dormant";
        const isEmpty = Number(bucket.totalEquity || 0) <= 0;
        const matchesMarginType = bucketMarginType === normalizedMarginType;
        const matchesBucketType = expectedIndex
          ? bucket.collateralAssetIndex === expectedIndex
          : true;

        return (isDormant || isEmpty) && matchesMarginType && matchesBucketType;
      }) || null
    );
  };

  // Removed debug logging and validation useEffect for better performance

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const handleRowClick = (bucketId: string, available_margin: number) => {
    if (onBucketSelect) {
      onBucketSelect(bucketId, available_margin);
    }
    setSelectedBucketId(bucketId);
  };

  const handleTransferClick = () => {
    setIsTransferModalOpen(true);
    setTransferFrom("");
    setTransferTo("");
    setTransferAmount(10);
    setCreateBucketAmount(10);
    setCreateBucketType("");
    setCreateMarginType("cross");
    setTransferMarginType("cross");
    setActiveTab("transfer");
  };

  const handleTransferCancel = () => {
    setIsTransferModalOpen(false);
    setTransferFrom("");
    setTransferTo("");
    setTransferAmount(10);
    setCreateBucketAmount(10);
    setCreateBucketType("");
    setCreateMarginType("cross");
    setTransferMarginType("cross");
    setActiveTab("transfer");
  };

  const handleFilterToggle = () => {
    setFilterMode((prev) => {
      // Cycle through: non-zero -> active -> dormant -> all -> non-zero
      switch (prev) {
        case "non-zero":
          return "active";
        case "active":
          return "dormant";
        case "dormant":
          return "all";
        case "all":
          return "non-zero";
        default:
          return "non-zero";
      }
    });
  };

  const handleTransferConfirm = async (params: TransferModalConfirmParams) => {
    // Check if mnemonic is provided first
    if (!mnemonic) {
      const errorMsg =
        "Mnemonic is required to perform transfers. Please provide a mnemonic to enable transfer functionality.";
      toast.error("Mnemonic required", {
        description: errorMsg,
        action: createErrorCopyAction(errorMsg),
      });
      return;
    }

    // Check if FiClient is initialized
    if (!exchangeClient || !exchangeReady) {
      const errorMsg =
        "Exchange client is still initializing. Please wait a moment and try again.";
      toast.error("Exchange client not ready", {
        description: errorMsg,
        action: createErrorCopyAction(errorMsg),
      });
      return;
    }

    setIsTransferring(true);

    // Show loading toast
    const toastId = toast.loading("Processing transfer...", {
      description: "Please wait while the transfer is being processed",
    });
    loadingToastIdRef.current = typeof toastId === "number" ? toastId : 0;

    try {
      const assetId = "USDC"; // Using USDC as specified
      let result;
      let successMessage = "";

      if (params.type === "transfer") {
        // Transfer tab: Handle transfers between buckets or spot wallet
        const { from, to, amount } = params;

        if (from === "spot-wallet" && to === "spot-wallet") {
          throw new Error("Cannot transfer from spot wallet to spot wallet");
        }

        if (from === "spot-wallet") {
          // When from is spot-wallet, to must be a bucket (selected by user)
          if (!to || to === "spot-wallet") {
            throw new Error("Please select a destination bucket");
          }

          // Use the selected bucket directly - its marginType is already determined
          result = await exchangeClient.extractToBucket({
            bucketId: to,
            assetId,
            amount: amount,
          });

          if (!result.success) {
            const errorMsg =
              result.error_message || "Failed to transfer to bucket";
            // Dismiss loading toast
            if (loadingToastIdRef.current !== 0) {
              toast.dismiss(loadingToastIdRef.current);
              loadingToastIdRef.current = 0;
            }
            toast.error("Transfer failed", {
              description: errorMsg,
              action: createErrorCopyAction(errorMsg),
            });
            return; // Stop the flow on error
          }

          const txHash = (result as ExtractToBucketResponse).txhash || "";
          successMessage = `Successfully transferred ${formatCurrency(amount)} from Spot Wallet to ${to}${txHash ? `\nTxHash: ${txHash}` : ""}`;
        } else if (to === "spot-wallet") {
          // Bucket → Spot wallet: withdrawFromBucket
          result = await exchangeClient.withdrawFromBucket({
            bucketId: from,
            assetId,
            amount: amount,
          });
          const txHash = (result as WithdrawFromBucketResponse).txhash || "";
          successMessage = `Successfully transferred ${formatCurrency(amount)} from ${from} to Spot Wallet${txHash ? `\nTxHash: ${txHash}` : ""}`;
        } else {
          // Bucket → Bucket: bucketTransfer
          if (!to) {
            throw new Error("Please select a destination bucket");
          }
          result = await exchangeClient.bucketTransfer({
            fromBucketId: from,
            toBucketId: to,
            assetId,
            amount: amount,
          });
          const txHash = (result as BucketTransferResponse).txhash || "";
          successMessage = `Successfully transferred ${formatCurrency(amount)} from ${from} to ${to}${txHash ? `\nTxHash: ${txHash}` : ""}`;
        }
      } else {
        // Create Bucket tab: Create a new bucket (or use existing dormant/empty bucket)
        const { from, bucketType, marginType, amount } = params;

        // Step 1: Check if there's an existing dormant or empty bucket matching requirements
        const existingBucket = findDormantOrEmptyBucket(marginType, bucketType);
        let targetBucketId: string;

        if (existingBucket) {
          // Use existing bucket instead of creating a new one
          targetBucketId = existingBucket.bucketId;
          console.log(
            `[BucketCollection] Using existing ${marginType} ${bucketType} bucket: ${targetBucketId}`,
          );
          successMessage = `Using existing ${marginType} margin bucket (${bucketType}): ${targetBucketId}`;
        } else {
          // Create new bucket (params aligned with create_bucket_signable_vs_http_body.md)
          const createResult = await exchangeClient.createBucket({
            collateralAssetIndex: assetSymbolToCollateralIndex(bucketType),
            marginType: marginType,
            initialMargin: "0",
          });

          if (!createResult.success) {
            throw new Error(createResult.message || "Failed to create bucket");
          }

          targetBucketId = createResult.bucketId ?? "";
          console.log(
            `[BucketCollection] Created new ${marginType} ${bucketType} bucket: ${targetBucketId}`,
          );
          successMessage = `Successfully created ${marginType} margin bucket (${bucketType})`;
        }

        // Step 2: If funds were specified, transfer them to the bucket
        if (amount > 0 && from === "spot-wallet") {
          // Extract funds from spot wallet to the bucket
          const extractResult = await exchangeClient.extractToBucket({
            bucketId: targetBucketId,
            assetId: bucketType,
            amount: amount,
          });

          if (!extractResult.success) {
            // Bucket was found/created but fund extraction failed
            throw new Error(
              `${existingBucket ? "Bucket found" : "Bucket created"} but failed to transfer funds: ${extractResult.error_message || "Unknown error"}`,
            );
          }

          const txHash = extractResult.txhash || "";
          successMessage = `${existingBucket ? "Using existing" : "Successfully created"} ${marginType} margin bucket (${bucketType}) and transferred ${formatCurrency(amount)}${txHash ? `\nTxHash: ${txHash}` : ""}`;
        } else if (amount > 0 && from !== "spot-wallet") {
          // Transfer from existing bucket to target bucket
          // Source bucket ID is already provided in 'from' parameter (selected by user)
          const transferResult = await exchangeClient.bucketTransfer({
            fromBucketId: from,
            toBucketId: targetBucketId,
            assetId: bucketType,
            amount: amount,
          });

          if (!transferResult.success) {
            // Bucket was found/created but transfer failed
            throw new Error(
              `${existingBucket ? "Bucket found" : "Bucket created"} but failed to transfer funds: ${transferResult.message || "Unknown error"}`,
            );
          }

          const txHash = transferResult.txhash || "";
          successMessage = `${existingBucket ? "Using existing" : "Successfully created"} ${marginType} margin bucket (${bucketType}) and transferred ${formatCurrency(amount)}${txHash ? `\nTxHash: ${txHash}` : ""}`;
        }
      }
      // Success - refresh data and close modal
      console.log("Transfer successful:", result);

      // Dismiss loading toast
      if (loadingToastIdRef.current !== 0) {
        toast.dismiss(loadingToastIdRef.current);
        loadingToastIdRef.current = 0;
      }

      // Show success toast
      toast.success("Transfer completed", {
        description: successMessage,
      });

      // Explicitly refresh spot wallet balance for immediate update
      // await refreshSpotWalletBalance();
      // Close modal and reset state
      setIsTransferModalOpen(false);
      setTransferFrom("");
      setTransferTo("");
      setTransferAmount(10);
      setCreateBucketAmount(10);
      setCreateBucketType("");
      setCreateMarginType("cross");
      setTransferMarginType("cross");


      // Wait 1 second before refreshing to allow backend to process the transaction
      await new Promise((resolve) => setTimeout(resolve, 1000));
      // Refresh bucket collections to show updated balances
      refreshBucketBalance();

    } catch (err) {
      // Dismiss loading toast
      if (loadingToastIdRef.current !== 0) {
        toast.dismiss(loadingToastIdRef.current);
        loadingToastIdRef.current = 0;
      }

      const errorMessage =
        err instanceof Error ? err.message : "Transfer failed";
      toast.error("Transfer failed", {
        description: errorMessage,
        action: createErrorCopyAction(errorMessage),
      });
      console.error("Transfer error:", err);
    } finally {
      setIsTransferring(false);
    }
  };

  // Prepare options for transfer modal - include spot wallet
  const transferOptions = useMemo(() => {
    const bucketOptions = sortedCollections.map((collection: Bucket) => ({
      value: collection.bucketId,
      label: `${collection.bucketId} (${formatCurrency(collection.totalEquity)})`,
    }));
    // Add spot wallet option at the beginning
    return [
      {
        value: "spot-wallet",
        label: `Spot Wallet (${formatCurrency(spotWalletBalance)})`,
      },
      ...bucketOptions,
    ];
  }, [sortedCollections, spotWalletBalance]);

  // Prepare buckets data for transfer modal
  const bucketsData = useMemo(() => {
    return sortedCollections.map((collection: Bucket) => ({
      bucketId: collection.bucketId,
      totalEquity: collection.totalEquity,
    }));
  }, [sortedCollections]);

  // Show appropriate message when wallet address is not available
  if (!walletAddress || walletAddress.trim() === "") {
    return (
      <WarningMessage message="Wallet address required to view bucket collections" />
    );
  }

  // Only show loading message on initial load when there's no data yet
  // Don't show loading when modal is open or when we already have data (background refresh)
  if (loading && !isTransferModalOpen && collections.length === 0) {
    return <LoadingMessage message="Loading bucket collections..." />;
  }

  if (error) {
    return <ErrorMessage error={error} />;
  }

  return (
    <div className="layout-container" style={{ height }}>
      {/* Header */}
      <LayoutHeader title="BUCKET COLLECTIONS">
        <div
          onClick={handleFilterToggle}
          className="btn-tab"
          title={
            filterMode === "non-zero"
              ? "Click to show active buckets"
              : filterMode === "active"
                ? "Click to show dormant buckets"
                : filterMode === "dormant"
                  ? "Click to show all buckets"
                  : "Click to show non-zero balance buckets"
          }
        >
          <span>
            {filterMode === "all"
              ? "All"
              : filterMode === "non-zero"
                ? "Non-Zero"
                : filterMode === "dormant"
                  ? "Dormant"
                  : "Active"}
          </span>
        </div>
        <div onClick={handleTransferClick} className="btn-tab">
          <img src="/transfer-repu.svg" />
          <span>Transfer</span>
        </div>
        {!isConnected && (
          <div
            onClick={async () => {
              if (refreshBucketBalance) {
                await refreshBucketBalance();
              }
            }}
            className="btn-tab"
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
            title="WebSocket disconnected - Click to refresh bucket collections"
          >
            <img
              src="/caution-icy.svg"
              alt="Warning"
              style={{ width: "1rem", height: "1rem" }}
            />
            <span>Refresh</span>
          </div>
        )}
      </LayoutHeader>

      {/* Table */}
      <div className="table-container shrinkable">
        {/* Table Header */}
        <div
          className="table-header"
          style={{ gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr" }}
        >
          <TableHeaderCell
            field="bucketId"
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
            left
          >
            Bucket ID
          </TableHeaderCell>
          <TableHeaderCell
            field="marginType"
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          >
            Margin Type
          </TableHeaderCell>
          <TableHeaderCell
            field="usedMargin"
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          >
            Used Margin
          </TableHeaderCell>
          <TableHeaderCell
            field="totalEquity"
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          >
            Total Equity
          </TableHeaderCell>
          <TableHeaderCell
            field="riskRatio"
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
            left={false}
          >
            Risk Ratio
          </TableHeaderCell>
        </div>
        {/* Table Body */}
        {sortedCollections.length === 0 ? (
          <EmptyMessage
            message={
              collections.length === 0
                ? "No bucket collections found. Make sure your wallet address is correct and has buckets."
                : `Filtered out all ${collections.length} collections (invalid data structure)`
            }
          />
        ) : (
          sortedCollections.map((collection: Bucket, index: number) => {
            const riskRatio = calculateRiskRatio(collection);
            const displayMarginType = getDisplayMarginType(collection);
            return (
              <div
                key={`collection-${collection.bucketId}-${index}`}
                onClick={() =>
                  handleRowClick(
                    collection.bucketId,
                    collection.availableMargin,
                  )
                }
                className={`table-row clickable ${index % 2 === 0 ? "even" : ""} ${selectedBucketId === collection.bucketId ? "selected" : ""}`}
                style={{ gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr" }}
              >
                <div className="table-cell primary">
                  {collection.bucketId || "---"}
                </div>
                <div className="table-cell">
                  {displayMarginType.toUpperCase()}
                </div>
                <div className="table-cell right monospace">
                  {formatCurrency(collection.usedMargin || 0)}
                </div>
                <div className="table-cell right monospace">
                  {formatCurrency(collection.totalEquity || 0)}
                </div>
                <div
                  className="table-cell right monospace"
                  style={{
                    color: riskRatio > 0.5 ? "#ff6b6b" : "#51cf66",
                    fontWeight: 500,
                  }}
                >
                  {formatPercentage(riskRatio)}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Transfer Modal */}
      <TransferModal
        isOpen={isTransferModalOpen}
        fromValue={transferFrom}
        toValue={transferTo}
        options={transferOptions}
        bucketsData={bucketsData}
        transferAmount={transferAmount}
        createBucketAmount={createBucketAmount}
        createBucketType={createBucketType}
        createMarginType={createMarginType}
        transferMarginType={transferMarginType}
        onFromChange={setTransferFrom}
        onToChange={setTransferTo}
        onTransferAmountChange={setTransferAmount}
        onCreateBucketAmountChange={setCreateBucketAmount}
        onCreateBucketTypeChange={setCreateBucketType}
        onCreateMarginTypeChange={setCreateMarginType}
        onTransferMarginTypeChange={setTransferMarginType}
        onConfirm={handleTransferConfirm}
        onCancel={handleTransferCancel}
        spotWalletBalance={spotWalletBalance}
        activeTab={activeTab}
        onActiveTabChange={setActiveTab}
        isTransferring={isTransferring}
        onOpen={refreshBucketBalance}
        collections={sortedCollections.map((collection: Bucket) => ({
          bucketId: collection.bucketId,
          marginType: collection.marginType,
        }))}
      />
    </div>
  );
}

export default React.memo(BucketCollections);
